/**
 * FeedbackGate.jsx — Golden RPG notification overlay for the Feedback page.
 * Corners use explicit pre-drawn SVG paths (no CSS transforms) — same as Inventory.jsx
 */

import React, { useEffect, useRef } from "react"
import "./FeedbackGate.css"

const CornerSVG = ({ gold = false }) => {
  const c1  = gold ? "#8a6018" : "rgba(59,191,255,.6)"
  const c2  = gold ? "#c9962a" : "rgba(59,191,255,.3)"
  const dot = gold ? "#f5b731" : "#3bbfff"
  const vn  = gold ? "#d4a830" : "rgba(59,191,255,.5)"

  const W = 90, H = 90
  const style = { position:"absolute", display:"block", pointerEvents:"none", overflow:"visible", width:W, height:H }

  /* ── Top-left: arms go RIGHT and DOWN ── */
  const TL = () => (
    <svg style={{...style, top:-5, left:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 86 L4 10 Q4 4 10 4 L86 4" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M9 80 L9 14 Q9 9 14 9 L80 9" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="8" cy="8" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="8" cy="8" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M16 4 Q22 10 16 17 Q23 13 30 17 Q25 10 30 4 Q23 8 16 4Z" fill={c1}/>
      <path d="M4 16 Q10 22 17 16 Q13 23 17 30 Q10 25 4 30 Q8 23 4 16Z" fill={c1}/>
      <circle cx="16" cy="16" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="16" cy="16" r="2.5" fill={dot}/>
      <circle cx="26" cy="8" r="3.2" fill={vn}/>
      <circle cx="21" cy="5" r="2" fill={dot} opacity=".85"/>
      <circle cx="8" cy="26" r="3.2" fill={vn}/>
      <circle cx="5" cy="21" r="2" fill={dot} opacity=".85"/>
      <path d="M8 38 Q11 30 16 22" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M38 8 Q30 11 22 16" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="10" cy="40" r="2" fill={dot} opacity=".5"/>
      <circle cx="40" cy="10" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Top-right: arms go LEFT and DOWN — mirrored on X axis ── */
  const TR = () => (
    <svg style={{...style, top:-5, right:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M86 86 L86 10 Q86 4 80 4 L4 4" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M81 80 L81 14 Q81 9 76 9 L10 9" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="82" cy="8" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="82" cy="8" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M74 4 Q68 10 74 17 Q67 13 60 17 Q65 10 60 4 Q67 8 74 4Z" fill={c1}/>
      <path d="M86 16 Q80 22 73 16 Q77 23 73 30 Q80 25 86 30 Q82 23 86 16Z" fill={c1}/>
      <circle cx="74" cy="16" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="74" cy="16" r="2.5" fill={dot}/>
      <circle cx="64" cy="8" r="3.2" fill={vn}/>
      <circle cx="69" cy="5" r="2" fill={dot} opacity=".85"/>
      <circle cx="82" cy="26" r="3.2" fill={vn}/>
      <circle cx="85" cy="21" r="2" fill={dot} opacity=".85"/>
      <path d="M82 38 Q79 30 74 22" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M52 8 Q60 11 68 16" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="80" cy="40" r="2" fill={dot} opacity=".5"/>
      <circle cx="50" cy="10" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Bottom-left: arms go RIGHT and UP — mirrored on Y axis ── */
  const BL = () => (
    <svg style={{...style, bottom:-5, left:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4 L4 80 Q4 86 10 86 L86 86" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M9 10 L9 76 Q9 81 14 81 L80 81" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="8" cy="82" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="8" cy="82" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M16 86 Q22 80 16 73 Q23 77 30 73 Q25 80 30 86 Q23 82 16 86Z" fill={c1}/>
      <path d="M4 74 Q10 68 17 74 Q13 67 17 60 Q10 65 4 60 Q8 67 4 74Z" fill={c1}/>
      <circle cx="16" cy="74" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="16" cy="74" r="2.5" fill={dot}/>
      <circle cx="26" cy="82" r="3.2" fill={vn}/>
      <circle cx="21" cy="85" r="2" fill={dot} opacity=".85"/>
      <circle cx="8" cy="64" r="3.2" fill={vn}/>
      <circle cx="5" cy="69" r="2" fill={dot} opacity=".85"/>
      <path d="M8 52 Q11 60 16 68" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M38 82 Q30 79 22 74" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="10" cy="50" r="2" fill={dot} opacity=".5"/>
      <circle cx="40" cy="80" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Bottom-right: arms go LEFT and UP — mirrored on both axes ── */
  const BR = () => (
    <svg style={{...style, bottom:-5, right:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M86 4 L86 80 Q86 86 80 86 L4 86" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M81 10 L81 76 Q81 81 76 81 L10 81" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="82" cy="82" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="82" cy="82" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M74 86 Q68 80 74 73 Q67 77 60 73 Q65 80 60 86 Q67 82 74 86Z" fill={c1}/>
      <path d="M86 74 Q80 68 73 74 Q77 67 73 60 Q80 65 86 60 Q82 67 86 74Z" fill={c1}/>
      <circle cx="74" cy="74" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="74" cy="74" r="2.5" fill={dot}/>
      <circle cx="64" cy="82" r="3.2" fill={vn}/>
      <circle cx="69" cy="85" r="2" fill={dot} opacity=".85"/>
      <circle cx="82" cy="64" r="3.2" fill={vn}/>
      <circle cx="85" cy="69" r="2" fill={dot} opacity=".85"/>
      <path d="M82 52 Q79 60 74 68" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M52 82 Q60 79 68 74" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="80" cy="50" r="2" fill={dot} opacity=".5"/>
      <circle cx="50" cy="80" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  return (
    <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:10, overflow:"visible" }} aria-hidden="true">
      <TL/><TR/><BL/><BR/>
    </div>
  )
}


// Alias used in FeedbackGate — always gold
const Corners = () => <CornerSVG gold />

/* ── Alert circle-i ───────────────────────────────────────────────────── */
const AlertIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24"
    fill="none" stroke="#d4d4d4"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8"  x2="12" y2="12" />
    <circle cx="12" cy="16" r="0.5" fill="#d4d4d4" />
  </svg>
)

/* ── Main component ───────────────────────────────────────────────────── */
const FeedbackGate = ({ status, feedType, score = 0, onAccept, onDecline, onClose }) => {
  const cardRef  = useRef(null)
  const isRules  = status === "rules"
  const isPlayed = status === "played"
  const visible  = isRules || isPlayed

  useEffect(() => {
    if (visible && cardRef.current) {
      cardRef.current.style.animation = "none"
      void cardRef.current.offsetHeight
      cardRef.current.style.animation = ""
    }
  }, [visible])

  if (!visible) return null

  const feedLabel = feedType === "network" ? "Network" : "Event"

  const rulesList = [
    "Feedback can only be submitted once",
    "Your responses are completely anonymous",
    "Completing the feedback earns you leaderboard points",
    `This survey covers the ${feedLabel} experience at Converge`,
    "All questions must be answered before submitting",
  ]

  const scoreBlock = isPlayed
    ? (score > 0
        ? (
          <div className="fg-score-display">
            <div className="fg-score-num">{score.toLocaleString()}</div>
            <div className="fg-score-label">POINTS EARNED</div>
          </div>
        )
        : (
          <p className="fg-body" style={{ color:"rgba(255,255,255,.4)", fontStyle:"italic", marginTop:4 }}>
            No points recorded for this submission.
          </p>
        )
      )
    : null

  return (
    <div
      className="fg-overlay fg-overlay--active"
      onClick={e => { if (e.target === e.currentTarget) isPlayed ? onClose?.() : onDecline?.() }}
      role="dialog"
      aria-modal="true"
      aria-label={isRules ? "Feedback Quest Rules" : "Feedback Already Submitted"}
    >
      <div className="fg-card" ref={cardRef}>

        {/* 4 pre-drawn corners — no CSS transforms */}
        <Corners />

        {/* Inner inset border */}
        <div className="fg-inner-border" />

        {/* Top rule */}
        <div className="fg-rule" />

        {/* Header */}
        <div className="fg-header">
          <AlertIcon />
          <span className="fg-alert-text">{isRules ? "QUEST" : "ALERT"}</span>
        </div>

        {/* Bottom rule */}
        <div className="fg-rule" />

        {/* Headline */}
        <div className={`fg-message${isPlayed ? " fg-message--nudge" : ""}`}>
          {isRules ? `${feedLabel.toUpperCase()} FEEDBACK QUEST` : "ALREADY SUBMITTED!"}
        </div>

        {/* Body */}
        <p className="fg-body">
          {isRules
            ? `Complete the ${feedLabel} Feedback quest to earn leaderboard points. Please read the rules before accepting.`
            : `You've already submitted your ${feedLabel} Feedback. Here are your points:`}
        </p>

        {/* Rules list */}
        {isRules && (
          <ul className="fg-rules">
            {rulesList.map((rule, i) => <li key={i}>{rule}</li>)}
          </ul>
        )}

        {/* Score (played mode) */}
        {scoreBlock}

        {/* Buttons */}
        <div className="fg-btns">
          {isRules ? (
            <>
              <button className="fg-btn-decline" onClick={onDecline}>DECLINE</button>
              <button className="fg-btn-accept"  onClick={onAccept}>ACCEPT &amp; START &#x2192;</button>
            </>
          ) : (
            <button className="fg-btn-close" onClick={onClose}>CLOSE</button>
          )}
        </div>

      </div>
    </div>
  )
}

export default FeedbackGate